/**
 * LocationPicker - choose a point by pointing at it.
 *
 * WHY THIS EXISTS
 * The 3PL forms used to ask an operator to TYPE a latitude and a longitude.
 * Those were the only two boxes on either form that fail silently: a transposed
 * pair still saves, still passes every range check, and the only symptom is a
 * partner who is never matched to a delivery, or a rider dispatched from a
 * point in the Indian Ocean. A pin dropped on a map cannot be transposed.
 *
 * The resolved figures stay printed under the map on purpose. The map is the
 * safer way to CHOOSE a point; the numbers are still the only way to check one
 * against a document, quote it in a ticket, or paste it somewhere else.
 *
 * The component is controlled: it owns no coordinate of its own, it reports
 * every move through onChange and re-reads what the parent gives back.
 */
import React, { useEffect, useRef, useState } from "react";
import { useGoogleMaps } from "../GoogleMapsContext";
import { colors, font, text, inp } from "../views/ThreePL/partnerTheme";

/* Uganda, where every partner on this dashboard operates. Used as the opening
   view when the form has no point yet, and as the country bias on the search
   box so "Nakawa" means the one in Kampala. */
const KAMPALA = { lat: 0.3476, lng: 32.5825 };
const COUNTRY = "ug";

/* Six decimal places is about 0.11 m on the ground, finer than any delivery
   address needs and far finer than a dragged pin. Rounding here keeps the
   readout short enough to read at a glance and keeps the value stable, so a
   pin that has not moved does not report a new coordinate on every event. */
const PRECISION = 6;
const round = (n) => Number(Number(n).toFixed(PRECISION));

const isPoint = (p) =>
  !!p && Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng));

/* One string per point, so effects can depend on the coordinate itself rather
   than on an object literal the parent rebuilds on every render. */
const keyOf = (lat, lng) => `${lat},${lng}`;

const LocationPicker = ({
  value,
  onChange,
  radiusKm,
  label,
  help,
  defaultCenter,
}) => {
  /* The provider exposes exactly one thing, { mapsApiLoaded }. It is undefined
     if this ever renders outside the provider, which must degrade to the typed
     fallback rather than throw. */
  const maps = useGoogleMaps();
  const mapsApiLoaded = !!(maps && maps.mapsApiLoaded);

  const mapNodeRef = useRef(null);
  const searchNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const listenersRef = useRef([]);
  const [mapReady, setMapReady] = useState(false);

  const hasValue = isPoint(value);
  const lat = hasValue ? Number(value.lat) : null;
  const lng = hasValue ? Number(value.lng) : null;
  const valueKey = hasValue ? keyOf(lat, lng) : "";

  /* WHY a ref: the map and marker listeners are attached once, when the map is
     built. A handler that closed over onChange directly would keep calling the
     very first render's onChange for the life of the form. */
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  /* Where the map opens. Read once, because re-centring on every render would
     yank the view back while the operator is panning. */
  const startRef = useRef(null);
  if (startRef.current === null) {
    if (hasValue) startRef.current = { lat, lng };
    else if (isPoint(defaultCenter))
      startRef.current = {
        lat: Number(defaultCenter.lat),
        lng: Number(defaultCenter.lng),
      };
    else startRef.current = KAMPALA;
  }

  /* ---- Build the map once the API is up ------------------------------- */
  useEffect(() => {
    if (!mapsApiLoaded) return undefined;
    const g = window.google;
    const node = mapNodeRef.current;
    const searchNode = searchNodeRef.current;
    if (!g || !g.maps || !node) return undefined;

    const map = new g.maps.Map(node, {
      center: startRef.current,
      zoom: 13,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      /* WHY off: a tap on a Google point of interest opens its info window and
         swallows the click, so the pin would not move where the operator
         aimed, which is the one thing this control has to get right. */
      clickableIcons: false,
    });
    const marker = new g.maps.Marker({
      position: startRef.current,
      map,
      draggable: true,
      title: "Drag me, or click anywhere on the map",
    });

    mapRef.current = map;
    markerRef.current = marker;

    const emit = (latLng) => {
      if (!latLng) return;
      const next = { lat: round(latLng.lat()), lng: round(latLng.lng()) };
      marker.setPosition(next);
      /* The circle follows the pin here as well as in its own effect, so the
         coverage preview never lags a drag by a render. */
      if (circleRef.current) circleRef.current.setCenter(next);
      if (typeof onChangeRef.current === "function") onChangeRef.current(next);
    };

    listenersRef.current.push(map.addListener("click", (e) => emit(e.latLng)));
    listenersRef.current.push(
      marker.addListener("dragend", (e) => emit(e.latLng))
    );

    /* The search box. Places is already requested by the provider's script
       tag, but guard anyway: if the library is missing the map still works and
       only the typing shortcut is gone. */
    if (g.maps.places && g.maps.places.Autocomplete && searchNode) {
      const autocomplete = new g.maps.places.Autocomplete(searchNode, {
        componentRestrictions: { country: COUNTRY },
        fields: ["geometry", "name"],
      });
      listenersRef.current.push(
        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          const location =
            place && place.geometry ? place.geometry.location : null;
          if (!location) return;
          map.setCenter(location);
          map.setZoom(15);
          emit(location);
        })
      );
    }

    setMapReady(true);

    return () => {
      listenersRef.current.forEach((listener) => {
        if (listener && typeof listener.remove === "function") listener.remove();
      });
      listenersRef.current = [];
      /* Places attaches its own listeners to the input element, and the input
         outlives the map on a re-render, so clear them by hand. */
      if (g.maps.event && searchNode) {
        g.maps.event.clearInstanceListeners(searchNode);
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      mapRef.current = null;
      /* No setState here on purpose: this cleanup runs during unmount, and a
         state update then is a warning and a no-op. Every reader of the map
         null checks the refs above instead. */
    };
  }, [mapsApiLoaded]);

  /* ---- Follow the value when it changes from outside ------------------- */
  useEffect(() => {
    if (!mapReady || lat === null || lng === null) return;
    const marker = markerRef.current;
    const map = mapRef.current;
    if (!marker || !map) return;
    const current = marker.getPosition();
    /* WHY the comparison: this effect also runs on the value we just emitted
       ourselves, and re-seating the marker mid-gesture fights the drag. */
    const same =
      current &&
      Math.abs(current.lat() - lat) < 1e-7 &&
      Math.abs(current.lng() - lng) < 1e-7;
    if (same) return;
    const next = { lat, lng };
    marker.setPosition(next);
    map.panTo(next);
    if (circleRef.current) circleRef.current.setCenter(next);
  }, [lat, lng, mapReady]);

  /* ---- The coverage circle -------------------------------------------- */
  const km = Number(radiusKm);
  const hasRadius = Number.isFinite(km) && km > 0;
  useEffect(() => {
    if (!mapReady) return;
    const g = window.google;
    const map = mapRef.current;
    if (!g || !g.maps || !map) return;

    if (!hasRadius) {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      return;
    }

    const centre = markerRef.current
      ? markerRef.current.getPosition()
      : startRef.current;

    if (circleRef.current) {
      circleRef.current.setRadius(km * 1000);
      if (centre) circleRef.current.setCenter(centre);
      return;
    }
    circleRef.current = new g.maps.Circle({
      map,
      center: centre || startRef.current,
      radius: km * 1000,
      strokeColor: colors.green,
      strokeOpacity: 0.9,
      strokeWeight: 1,
      fillColor: colors.green,
      fillOpacity: 0.12,
      /* WHY not clickable: the circle covers the part of the map the operator
         is most likely to click, and a click on it has to keep moving the
         pin rather than land on the shape. */
      clickable: false,
    });
  }, [hasRadius, km, mapReady]);

  /* ---- Typed fallback -------------------------------------------------- */
  /* The two boxes keep their own text because a half typed number is not a
     number: Number("0.") is 0, so echoing the parsed value back into the box
     would delete the decimal point as it is typed. */
  const [rawLat, setRawLat] = useState("");
  const [rawLng, setRawLng] = useState("");
  /* What we last sent up, so the sync below can tell our own echo from a real
     outside change such as the parent clearing the form after a save. */
  const lastEmittedRef = useRef(null);

  useEffect(() => {
    if (valueKey === lastEmittedRef.current) return;
    const parts = valueKey ? valueKey.split(",") : ["", ""];
    setRawLat(parts[0] || "");
    setRawLng(parts[1] || "");
  }, [valueKey]);

  const emitTyped = (nextLat, nextLng) => {
    const parsedLat =
      String(nextLat).trim() === "" ? NaN : Number(nextLat);
    const parsedLng =
      String(nextLng).trim() === "" ? NaN : Number(nextLng);
    const complete = Number.isFinite(parsedLat) && Number.isFinite(parsedLng);
    /* An emptied box reports null rather than leaving the last complete pair
       standing, so a half typed coordinate can never be submitted as the
       previous one. */
    lastEmittedRef.current = complete ? keyOf(parsedLat, parsedLng) : "";
    if (typeof onChange === "function") {
      onChange(complete ? { lat: parsedLat, lng: parsedLng } : null);
    }
  };

  const frame = {
    border: `1px solid ${colors.line}`,
    borderRadius: 10,
    overflow: "hidden",
    background: colors.ground,
  };

  const readout = (
    <div
      style={{
        marginTop: 8,
        padding: "8px 12px",
        border: `1px solid ${colors.line}`,
        borderRadius: 8,
        background: colors.lineSoft,
        display: "flex",
        flexWrap: "wrap",
        gap: 16,
        alignItems: "baseline",
      }}
    >
      {hasValue ? (
        <>
          <span style={{ ...text.numeric, fontFamily: font.mono }}>
            lat {lat}
          </span>
          <span style={{ ...text.numeric, fontFamily: font.mono }}>
            lng {lng}
          </span>
        </>
      ) : (
        <span style={{ ...text.body, color: colors.muted, fontSize: 13 }}>
          No point chosen yet. Search, click the map, or drag the pin.
        </span>
      )}
    </div>
  );

  if (!mapsApiLoaded) {
    return (
      <div>
        {label ? <span style={text.label}>{label}</span> : null}
        <div
          style={{
            border: `1px solid ${colors.amber}`,
            background: colors.amberSoft,
            borderRadius: 10,
            padding: "12px 14px",
            marginBottom: 12,
          }}
        >
          <p
            style={{
              ...text.body,
              margin: 0,
              color: colors.amber,
              fontWeight: 600,
            }}
          >
            The map could not be loaded.
          </p>
          <p style={{ ...text.body, margin: "4px 0 0", color: colors.body }}>
            Type the two figures instead. Kampala sits near {KAMPALA.lat},{" "}
            {KAMPALA.lng}.
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "0 20px",
          }}
        >
          <div style={{ marginBottom: 10 }}>
            <span style={text.label}>Latitude</span>
            <input
              type="number"
              step="any"
              min="-90"
              max="90"
              value={rawLat}
              onChange={(e) => {
                setRawLat(e.target.value);
                emitTyped(e.target.value, rawLng);
              }}
              placeholder={String(KAMPALA.lat)}
              {...inp({ fontFamily: font.mono })}
            />
            <p style={text.help}>Between -90 and 90.</p>
          </div>
          <div style={{ marginBottom: 10 }}>
            <span style={text.label}>Longitude</span>
            <input
              type="number"
              step="any"
              min="-180"
              max="180"
              value={rawLng}
              onChange={(e) => {
                setRawLng(e.target.value);
                emitTyped(rawLat, e.target.value);
              }}
              placeholder={String(KAMPALA.lng)}
              {...inp({ fontFamily: font.mono })}
            />
            <p style={text.help}>Between -180 and 180.</p>
          </div>
        </div>
        {help ? <p style={text.help}>{help}</p> : null}
      </div>
    );
  }

  return (
    <div>
      {label ? <span style={text.label}>{label}</span> : null}
      <div style={frame}>
        <div style={{ padding: 10, borderBottom: `1px solid ${colors.line}` }}>
          <input
            ref={searchNodeRef}
            type="text"
            placeholder="Search a place, for example Nakawa"
            /* WHY: this control lives inside a form, and Enter on a search box
               would submit the form with whatever is typed so far. The place
               is chosen from the dropdown, never by submitting. */
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
            {...inp()}
          />
        </div>
        <div ref={mapNodeRef} style={{ width: "100%", height: 320 }} />
      </div>
      {readout}
      {hasRadius ? (
        <p style={text.help}>
          The shaded circle is the {km} km covered from this point.
        </p>
      ) : null}
      {help ? <p style={text.help}>{help}</p> : null}
    </div>
  );
};

export default LocationPicker;
